import React, { useState } from 'react';
import Web3 from 'web3';
import BridgeContractABI from './MyContractABI.json'; // Bridge contract ABI
import TokenABI from './ERC20.json'; // ERC20 token ABI
import './App.css';

function Bridge() {
  const [web3, setWeb3] = useState(null);
  const [contract, setContract] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedToken, setSelectedToken] = useState('dai');
  const [approved, setApproved] = useState(false);
  
  const fntContract = '0xD029a934471AbF41A63E79036f4cE43c62A01b34';
  
  const contractAddresses = {
    dai: '0x8D11eC38a3EB5E956B052f67Da8Bdc9bef8Abf3E'
  };

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);
        const contractInstance = new web3Instance.eth.Contract(BridgeContractABI, fntContract // Bridge contract address
        );
        setContract(contractInstance);
        const daiApproved = await checkApprovalStatus(web3Instance, TokenABI, contractAddresses.dai);
        setApproved({ dai: daiApproved });
      } catch (error) {
        console.error('Error connecting to wallet:', error);
      }
    } else {
      console.error('Please install a wallet like Metamask.');
    }
  };

  const checkApprovalStatus = async (web3Instance, tokenABI, tokenAddress) => {
    try {
      const tokenContract = new web3Instance.eth.Contract(tokenABI, tokenAddress);
      const userAddress = (await web3Instance.eth.getAccounts())[0];
      const allowance = await tokenContract.methods.allowance(userAddress, fntContract).call();
      return allowance > 0;
    } catch (error) {
      console.error('Error checking approval status:', error);
      return false;
    }
  };

  const disconnectWallet = () => {
    setWeb3(null);
    setContract(null);
    console.log('Wallet disconnected');
  };

  const depositToken = async () => {
    if (selectedToken === 'fantom') {
      await depositFantom();
    } else {
      if (approved[selectedToken]) {
        if (web3 && contract) {
          try {
            const decimal = await getDecimalPrecision();
            const amount = (inputValue * 10 ** decimal).toString();
            const approvedAmount = await returnApprovedAmount();
            if (approvedAmount >= amount) {
              console.log('Going to deposit.');
              await contract.methods.exchangeToken(contractAddresses[selectedToken], amount).send({
                from: (await web3.eth.getAccounts())[0]
              });
              console.log(`${selectedToken.toUpperCase()} deposited successfully`);
            } else {
              console.log('Please approve the token first.');
              approveToken(selectedToken);
            }
          } catch (error) {
            console.error(`Error depositing ${selectedToken.toUpperCase()}:`, error);
          }
        } else {
          console.error('Please connect your wallet first.');
        }
      } else {
        console.log('Please approve the token first.');
        approveToken(selectedToken);
      }
    }
  };
  
  const returnApprovedAmount = async () => {
    if (selectedToken !== 'fantom') {
      const web3Instance = new Web3(window.ethereum); // Recreate Web3 instance
      const userAddress = (await web3.eth.getAccounts())[0];
      const tokenContract = new web3Instance.eth.Contract(TokenABI, contractAddresses[selectedToken]);
      const allowance = await tokenContract.methods.allowance(userAddress, fntContract).call();
      return allowance;
    }
    return 0;
  }

  const depositFantom = async () => {
    if (web3 && contract) {
      try {
        await contract.methods.exchange().send({
          from: (await web3.eth.getAccounts())[0],
          value: web3.utils.toWei(inputValue, 'ether')
        });
        console.log('Fantom deposited successfully');
      } catch (error) {
        console.error('Error depositing Fantom:', error);
      }
    } else {
      console.error('Please connect your wallet first.');
    }
  };

  const handleTokenAction = async () => {
    if (selectedToken === 'fantom') {
      await depositFantom();
    } else {
      await depositToken();
    }
  };

  const handleTokenChange = (e) => {
    setSelectedToken(e.target.value);
  };

  const approveToken = async (tokenType) => {
    if (web3 && contract) {
      try {
        const web3Instance = new Web3(window.ethereum); // Recreate Web3 instance
        const userAddress = (await web3.eth.getAccounts())[0];
        const decimal = await getDecimalPrecision();
        const amountToApprove = (inputValue * 10 ** decimal).toString();
        const tokenContract = new web3Instance.eth.Contract(TokenABI, contractAddresses[tokenType]);
        const allowance = await tokenContract.methods.allowance(userAddress, fntContract).call();
        if (allowance >= amountToApprove) {
          setApproved({ ...approved, [tokenType]: true });
        } else {
          const approvalReceipt = await tokenContract.methods.approve(fntContract, amountToApprove).send({
            from: userAddress
          });
          await approvalReceipt.on('confirmation', (confirmationNumber, receipt) => {
            console.log(`Transaction confirmed. Confirmation number: ${confirmationNumber}`);
            setApproved({ ...approved, [tokenType]: true });
            console.log(`${tokenType.toUpperCase()} approved successfully.`);
          });
        }
      } catch (error) {
        console.error(`Error approving ${tokenType.toUpperCase()}:`, error);
      }
    } else {
      console.error('Please connect your wallet first.');
    }
  };

  const getButtonText = () => {
    if (selectedToken === 'fantom') {
      return 'Deposit Fantom';
    } else {
      if (approved[selectedToken]) {
        return `Deposit ${selectedToken.toUpperCase()}`;
      } else {
        return 'Approve';
      }
    }
  };

  const getDecimalPrecision = async () => {
    if (web3) {
      try {
        const tokenContract = new web3.eth.Contract(TokenABI, contractAddresses[selectedToken]);
        const precision = await tokenContract.methods.decimals().call();
        return parseInt(precision);
      } catch (error) {
        console.error('Error fetching decimal precision:', error);
        return 18; // Default to 18 if unable to fetch precision
      }
    } else {
      console.error('Web3 instance not available.');
      return 18; // Default to 18 if web3 instance is not available
    }
  };

  return (
    <div>
      <header>
        <button onClick={web3 ? disconnectWallet : connectWallet}>
          {web3 ? 'Disconnect Wallet' : 'Connect Wallet'}
        </button>
      </header>
      <div className="container">
        <main className='input'>
          <h1 className="bridge">Bridge</h1>
          <div className="input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter amount.."
            />
            <div className="dropdown">
              <select onChange={handleTokenChange} value={selectedToken}>
                <option value="dai">DAI</option>
              </select>
            </div>
          </div>
          <div className='but'>
            <button onClick={handleTokenAction}>
              {getButtonText()}
            </button>
          </div>
        </main>
        <footer className="footer">
          <p>&copy; 2024 FantomToBinance. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default Bridge;